/**
 * The visitor's consent in both apps (ADR-141 points 4 and 5, F-127): pp-consent-manager, the
 * template's cookie banner, with the category "Statistik" for Faro (and on the website the Tag
 * Manager). Each app loads the script before its own (the portal through
 * `apps/portal/plugins/consent-manager.ts`, the website through astro-core's `head.astro`), so its
 * global `PPConsentManager` exists when the app's scripts run; the async loader the script fetches
 * adds the rest, `onReady` waits for it. The choice lives in cookies of each host
 * (`ppcm-consent-category-*`), the portal's apart from the website's. This module is the only
 * access to the global in both apps (review of 2026-10-23, N3).
 */

/** The part of the global `PPConsentManager` the apps use. */
export type ConsentManager = {
  /** Runs the callback once the async loader is there, at once if it already is. */
  onReady(callback: () => void): void;
  /** Runs the callback once "Statistik" is granted: at once if it already is, else at that moment. */
  onConsentStatistics(callback: () => void): void;
  /** Deletes every stored choice and reloads with the banner; added by the async loader. */
  revokeConsentAll?(): void;
};

/** Where the global lives; tests pass their own. */
export type ConsentHost = { PPConsentManager?: ConsentManager };

/** The consent manager, or null when its script did not load (blocked, failed). */
export function consentManager(host: ConsentHost = globalThis as ConsentHost): ConsentManager | null {
  return host.PPConsentManager ?? null;
}

/**
 * Runs `callback` once the visitor agrees to "Statistik", at once when they already have. Without
 * the consent manager it never runs: no banner, no consent, no statistics.
 */
export function onStatisticsConsent(callback: () => void, host?: ConsentHost): void {
  consentManager(host)?.onConsentStatistics(callback);
}

/**
 * Opens the consent settings again ("Cookie-Einstellungen"): the stored choice is withdrawn and
 * the page reloads with the banner. Showing the banner alone (`_showBanner`) cannot withdraw
 * "Statistik", because saving a smaller choice only adds cookies and removes none; and only the
 * reload stops statistics that already run.
 */
export function reopenConsent(host?: ConsentHost): void {
  const manager = consentManager(host);
  manager?.onReady(() => manager.revokeConsentAll?.());
}
